import React, { useState, useEffect } from "react";
import { Plus, Save } from "lucide-react";
import { hostsApi, podcastsApi } from "../../utils/api";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import ActionButtons, {
  PrimaryButton,
  SecondaryButton,
} from "../common/ActionButtons";

const HostManagement = () => {
  const [hosts, setHosts] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingHost, setEditingHost] = useState(null);

  const [formData, setFormData] = useState({
    status: "Active",
    title: "",
    topic: "",
    podCastID: 0,
  });

  const statusOptions = ["Active", "Inactive", "Guest", "Archived"];

  useEffect(() => {
    fetchHosts();
    fetchPodcasts();
  }, []);

  const fetchHosts = async () => {
    setLoading(true);
    try {
      const data = await hostsApi.getAll();
      setHosts(data);
    } catch (error) {
      console.error("Error fetching hosts:", error);
    }
    setLoading(false);
  };

  const fetchPodcasts = async () => {
    try {
      const data = await podcastsApi.getAll();
      setPodcasts(data);
    } catch (error) {
      console.error("Error fetching podcasts:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingHost) {
        await hostsApi.update(editingHost.id, formData);
      } else {
        await hostsApi.create(formData);
      }
      fetchHosts();
      resetForm();
    } catch (error) {
      console.error("Error saving host:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this host?")) return;
    setLoading(true);
    try {
      await hostsApi.delete(id);
      fetchHosts();
    } catch (error) {
      console.error("Error deleting host:", error);
    }
    setLoading(false);
  };

  const handleEdit = (host) => {
    setEditingHost(host);
    setFormData({
      status: host.status || "Active",
      title: host.title || "",
      topic: host.topic || "",
      podCastID: host.podCastID || 0,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      status: "Active",
      title: "",
      topic: "",
      podCastID: 0,
    });
    setEditingHost(null);
    setShowForm(false);
  };

  const getPodcastName = (podCastID) => {
    const podcast = podcasts.find((p) => p.id === podCastID);
    return podcast ? podcast.title : "N/A";
  };

  const filteredHosts = hosts.filter(
    (host) =>
      host.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      host.topic?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          onRefresh={fetchHosts}
          placeholder="Search hosts..."
        />
        <PrimaryButton onClick={() => setShowForm(!showForm)} icon={Plus}>
          New Host
        </PrimaryButton>
      </div>

      <FormModal
        title={editingHost ? "Edit Host" : "New Host"}
        isOpen={showForm}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Podcast *
            </label>
            <select
              required
              value={formData.podCastID}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  podCastID: parseInt(e.target.value),
                })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value={0}>Select Podcast</option>
              {podcasts.map((podcast) => (
                <option key={podcast.id} value={podcast.id}>
                  {podcast.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Host Name *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Bio / Description
            </label>
            <textarea
              value={formData.topic}
              onChange={(e) =>
                setFormData({ ...formData, topic: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={resetForm}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading} icon={Save}>
              {loading ? "Saving..." : "Save Host"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Podcast
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Bio
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredHosts.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  No hosts found. Create one to get started!
                </td>
              </tr>
            ) : (
              filteredHosts.map((host) => (
                <tr key={host.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">
                    {host.title}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {getPodcastName(host.podCastID)}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">
                    {host.topic || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        host.status === "Active"
                          ? "bg-green-100 text-green-800"
                          : host.status === "Guest"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-slate-100 text-slate-800"
                      }`}
                    >
                      {host.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    <ActionButtons
                      onEdit={() => handleEdit(host)}
                      onDelete={() => handleDelete(host.id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HostManagement;
